/**
 * The ten RSA 3D analyses, as 123BIMLive runs them. Shared by the 123BIMLive page (the
 * analyses story, which shows each on the simulated corridor) and the home page (the
 * analyses list). `key` is also the image base name under /images/123bimlive/, which the
 * list shows; `tint` colours its code badge.
 */
export const ANALYSES = [
  {
    key: "ssd",
    code: "SSD",
    tint: "#ff9a2e",
    title: "Stopping sight distance",
    does: "Tests at every station whether a driver can see an object in the lane in time to stop, stepping through the AASHTO speed table to the highest speed the view supports.",
    gets: "Where drivers can't stop in time, what blocks the view (terrain, vegetation, a crest, guardrail, pole or sign) and a crash-risk score for ranking the fixes.",
    alt: "A sight line measured ahead of a car on a two-lane highway",
  },
  {
    key: "psd",
    code: "PSD",
    tint: "#38bdf8",
    title: "Passing sight distance",
    does: "Compares the passing zones marked on the road with the sight distance a pass needs under AASHTO and MUTCD criteria.",
    gets: "Zones striped for passing where a driver can't see far enough to pass safely, the setting for head-on crashes.",
    alt: "A car preparing to pass a truck on a two-lane road",
  },
  {
    key: "isd",
    code: "ISD",
    tint: "#4ade80",
    title: "Intersection sight distance",
    does: "Checks each maneuver at each intersection against the AASHTO sight triangles for a vehicle waiting to turn or cross.",
    gets: "Which movements at which intersections have a blocked view, so clearing, signal or geometry work goes where it counts.",
    alt: "A sight triangle drawn at a rural intersection",
  },
  {
    key: "curves",
    code: "HCA",
    tint: "#c084fc",
    title: "Horizontal curves",
    does: "Measures each curve's radius and superelevation from the scan and checks them against the AASHTO Green Book, with maximum superelevation set per route.",
    gets: "Curves whose safe speed falls below the posted speed, the candidates for advisory speeds, chevrons or re-banking.",
    alt: "A car on a tight horizontal curve with its radius marked",
  },
  {
    key: "flat-spots",
    code: "FSA",
    tint: "#22d3ee",
    title: "Flat spots",
    does: "Measures the longitudinal grade and cross slope of the pavement at every station.",
    gets: "The flat spots where water stays on the road, so drainage fixes target hydroplaning and ice risk.",
    alt: "Standing water across a flat section of highway",
  },
  {
    key: "glare",
    code: "GLR",
    tint: "#facc15",
    title: "Sun glare",
    does: "Builds a year-long calendar for every station of the times the sun sits near the driver's line of sight.",
    gets: "Which stretches blind drivers, and at what times of day and year.",
    alt: "Low sun shining straight down a highway",
  },
  {
    key: "guardrail",
    code: "GRW",
    tint: "#fb7185",
    title: "Guardrail warrant",
    does: "Evaluates embankment height and fill slope against the barrier warrant in the AASHTO Roadside Design Guide.",
    gets: "Where a barrier is warranted along the corridor, and where one is not, before crews install or remove guardrail.",
    alt: "A steep embankment beside a mountain road, highlighted in red",
  },
  {
    key: "embankment",
    code: "CS3D",
    tint: "#a3e635",
    title: "Cross-Section 3D",
    does: "Cuts 3D cross-sections through the embankments along the corridor.",
    gets: "Side-slope geometry for designers, delivered as DXF cross-sections without sending a survey crew.",
    alt: "Embankment slopes along a divided highway, colored by steepness",
  },
  {
    key: "clear-zone",
    code: "CZ",
    tint: "#60a5fa",
    title: "Clear zone",
    does: "Measures the clear zone width and side slopes, and finds the fixed objects and guardrails inside it.",
    gets: "Trees, poles and slopes in the recovery area, where a vehicle that leaves the road has no room to recover.",
    alt: "The roadside recovery area beside a highway, green where clear and red where not",
  },
  {
    key: "signs",
    code: "TSA",
    tint: "#f472b6",
    title: "Traffic signs",
    does: "Detects signs in vehicle camera imagery, reads each one's MUTCD code and size, and places it with the LiDAR.",
    gets: "A georeferenced sign inventory for the corridor, without a crew walking it.",
    alt: "A stop sign detected and outlined in a camera frame",
  },
];
